import { X, Eye, Download, FileText, RefreshCw, ReceiptText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export function GstReportModal({
  show, onClose,
  gstDateFrom, setGstDateFrom, gstDateTo, setGstDateTo,
  fetchGstData, gstLoading, gstData,
  downloadGstPdf, downloadGstExcel,
}) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="gst-report-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-blue-50 shrink-0">
          <div>
            <h3 className="text-sm font-bold text-slate-800">GST Report - Day Wise Summary</h3>
            <p className="text-xs text-slate-500 mt-0.5">GSTIN: 33ABGFP3436L1Z3 | Tamil Nadu (33)</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-gst-report"><X className="w-4 h-4" /></Button>
        </div>
        <div className="px-6 py-3 border-b border-slate-100 flex items-end gap-3 flex-wrap shrink-0">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">From</label>
            <Input type="date" value={gstDateFrom} onChange={e => setGstDateFrom(e.target.value)} data-testid="gst-date-from" className="w-40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">To</label>
            <Input type="date" value={gstDateTo} onChange={e => setGstDateTo(e.target.value)} data-testid="gst-date-to" className="w-40" />
          </div>
          <Button size="sm" onClick={fetchGstData} disabled={gstLoading} data-testid="gst-view-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white">
            {gstLoading ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
            View Report
          </Button>
          <Button size="sm" variant="outline" onClick={downloadGstPdf} data-testid="gst-pdf-btn" className="border-blue-300 text-blue-700">
            <FileText className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={downloadGstExcel} data-testid="gst-excel-btn" className="border-green-300 text-green-700">
            <Download className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {!gstData && !gstLoading && (
            <div className="text-center text-slate-400 py-16">
              <ReceiptText className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Select date range and click "View Report"</p>
            </div>
          )}
          {gstLoading && (
            <div className="text-center py-16"><RefreshCw className="w-6 h-6 mx-auto animate-spin text-blue-500" /><p className="text-xs text-slate-400 mt-2">Loading GST data...</p></div>
          )}
          {gstData && (
            <div className="space-y-6">
              <div className="grid grid-cols-5 gap-3">
                {[{ label: "Total Orders", value: gstData.grand.order_count },
                  { label: "Total Items", value: gstData.grand.item_count },
                  { label: "Taxable Value", value: `\u20B9${gstData.grand.taxable.toLocaleString("en-IN", {minimumFractionDigits: 2})}` },
                  { label: "CGST + SGST", value: `\u20B9${gstData.grand.total_tax.toLocaleString("en-IN", {minimumFractionDigits: 2})}` },
                  { label: "Total Value", value: `\u20B9${gstData.grand.total_value.toLocaleString("en-IN", {minimumFractionDigits: 2})}` },
                ].map((c, i) => (
                  <div key={i} className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-center" data-testid={`gst-summary-${i}`}>
                    <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">{c.label}</p>
                    <p className="text-base font-bold text-slate-800 mt-1">{c.value}</p>
                  </div>
                ))}
              </div>

              <div>
                <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">GST Slab Summary</h4>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-[#1e4a80] text-white">
                      <tr>
                        {["GST Slab", "Items", "Taxable Value", "CGST", "SGST", "Total Tax"].map(h => (
                          <th key={h} className="px-3 py-2 text-right first:text-left font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(gstData.slab_totals).sort(([a],[b]) => Number(a) - Number(b)).map(([slab, v], i) => (
                        <tr key={slab} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="px-3 py-2 font-bold text-slate-700">{slab}%</td>
                          <td className="px-3 py-2 text-right">{v.count}</td>
                          <td className="px-3 py-2 text-right">{"\u20B9"}{v.taxable.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="px-3 py-2 text-right">{"\u20B9"}{v.cgst.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="px-3 py-2 text-right">{"\u20B9"}{v.sgst.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                          <td className="px-3 py-2 text-right font-bold">{"\u20B9"}{v.total_tax.toLocaleString("en-IN", {minimumFractionDigits: 2})}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold text-slate-700 mb-2 uppercase tracking-wide">Day-wise Detail</h4>
                {gstData.days.map((day, di) => (
                  <div key={di} className="mb-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-t-lg px-3 py-1.5">
                      <span className="text-xs font-bold text-[#1e4a80]">DATE: {day.date}</span>
                      <span className="text-[10px] text-slate-500 ml-3">{day.orders.length} orders | Taxable: {"\u20B9"}{day.taxable.toLocaleString("en-IN", {minimumFractionDigits: 2})} | Tax: {"\u20B9"}{day.total_tax.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
                    </div>
                    <div className="border border-t-0 border-slate-200 rounded-b-lg overflow-hidden">
                      <table className="w-full text-[11px]">
                        <thead className="bg-slate-100">
                          <tr>
                            {["Order", "Patient", "Item", "Qty", "Taxable", "GST%", "CGST", "SGST", "Total"].map(h => (
                              <th key={h} className="px-2 py-1.5 text-right first:text-left text-slate-600 font-semibold">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {day.orders.flatMap((order, oi) =>
                            order.items.map((item, ii) => (
                              <tr key={`${oi}-${ii}`} className={ii % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                                <td className="px-2 py-1 text-slate-600">{ii === 0 ? order.orderId : ""}</td>
                                <td className="px-2 py-1">{ii === 0 ? order.patientName : ""}</td>
                                <td className="px-2 py-1 font-medium">{item.name}</td>
                                <td className="px-2 py-1 text-center">{item.qty}</td>
                                <td className="px-2 py-1 text-right">{"\u20B9"}{item.taxable_value.toFixed(2)}</td>
                                <td className="px-2 py-1 text-center">{item.gst_rate}%</td>
                                <td className="px-2 py-1 text-right">{"\u20B9"}{item.cgst.toFixed(2)}</td>
                                <td className="px-2 py-1 text-right">{"\u20B9"}{item.sgst.toFixed(2)}</td>
                                <td className="px-2 py-1 text-right font-medium">{"\u20B9"}{item.total.toFixed(2)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
